import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useUsers } from "@/contexts/users-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamCreditsHistoryDTO } from "@shaarp/common";
import { KeyRound } from "lucide-react";
import { useEffect } from "react";

export default function DataTableCellTeamCreditsHistoryUser({
  history,
}: {
  history: TeamCreditsHistoryDTO;
}) {
  const { users, apiFetchUsers, apiFetchLoading } = useUsers();
  const { t } = useTranslation();

  useEffect(() => {
    const init = async () => {
      await apiFetchUsers();
    };
    init();
  }, [apiFetchUsers]);

  // Show skeleton while loading
  if (apiFetchLoading) {
    return (
      <div className="flex items-center">
        <Skeleton className="w-8 h-8 rounded-full" />
        <Skeleton className="ml-2 w-24 h-4" />
      </div>
    );
  }

  // API key attribution: the call came from a public API key, not a user
  if (!history.userId && history.apiKeyId) {
    return (
      <div className="flex items-center">
        <Avatar className="w-8 h-8">
          <AvatarFallback
            className="text-xs"
            aria-label={t(
              "admin.dataTableAdminTeams.dialogs.giftCredits.history.apiKey"
            )}
          >
            <KeyRound className="w-4 h-4" />
          </AvatarFallback>
        </Avatar>
        <span className="ml-2 text-sm font-mono">
          {history.apiKeyMasked ?? history.apiKeyId.slice(0, 8)}
        </span>
      </div>
    );
  }

  // Find the user in the users list
  const user = history.userId
    ? users.find((u) => u.id === history.userId)
    : null;

  if (!user) {
    return (
      <div className="flex items-center">
        <Avatar className="w-8 h-8">
          <AvatarFallback className="text-xs">
            {history.userId ? history.userId.slice(0, 2).toUpperCase() : "??"}
          </AvatarFallback>
        </Avatar>
        <span className="ml-2 text-sm text-muted-foreground">
          {t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.unknownUser"
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <Avatar className="w-8 h-8">
        <AvatarImage src={user.imageUrl} alt={user.fullName} />
        <AvatarFallback className="text-xs">
          {user.fullName
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2)}
        </AvatarFallback>
      </Avatar>
      <span className="ml-2 text-sm">{user.fullName}</span>
    </div>
  );
}
